import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customersAPI } from '../../services/api';
import { useCompany } from '../CompanyContext';
import { Customer, CreateCustomerDto, UpdateCustomerDto, CustomerStatus, CustomerType } from '../../types';
import {
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess } from '../../utils/toast';

const CustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedCompany } = useCompany();
  
  // Get company currency for formatting
  const companyCurrency = selectedCompany?.defaultCurrency?.code || 'USD';
  const currencySymbol = selectedCompany?.defaultCurrency?.symbol || '$';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateCustomerDto>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    type: CustomerType.INDIVIDUAL,
    status: CustomerStatus.ACTIVE,
    taxId: '',
    website: '',
    notes: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    creditLimit: 0,
    currentBalance: 0,
    paymentTerms: '',
    companyId: '',
  });

  useEffect(() => {
    if (selectedCompany) {
      setFormData(prev => ({
        ...prev,
        companyId: selectedCompany.id,
      }));
      
      if (id) {
        loadFormData();
      }
    }
  }, [selectedCompany, id]);

  const loadFormData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const customer = await customersAPI.getById(id);
      setFormData({
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        country: customer.country || '',
        postalCode: customer.postalCode || '',
        type: customer.type,
        status: customer.status,
        taxId: customer.taxId || '',
        website: customer.website || '',
        notes: customer.notes || '',
        contactPerson: customer.contactPerson || '',
        contactPhone: customer.contactPhone || '',
        contactEmail: customer.contactEmail || '',
        creditLimit: typeof customer.creditLimit === 'string' ? parseFloat(customer.creditLimit) : customer.creditLimit,
        currentBalance: typeof customer.currentBalance === 'string' ? parseFloat(customer.currentBalance) : customer.currentBalance,
        paymentTerms: customer.paymentTerms || '',
        companyId: customer.companyId,
      });
    } catch (err) {
      setError('Failed to load customer');
      console.error('Error loading customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle number fields properly - empty string should be undefined, not 0
    let processedValue: any = value;
    if (type === 'number') {
      processedValue = value === '' ? undefined : parseFloat(value);
      // If parseFloat returns NaN, set to undefined
      if (isNaN(processedValue)) {
        processedValue = undefined;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const getFieldError = (fieldName: string) => {
    return fieldErrors[fieldName] || '';
  };

  const getFieldClassName = (fieldName: string) => {
    const baseClass = "mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500";
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-300 focus:ring-red-500 focus:border-red-500`
      : baseClass;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    try {
      setSaving(true);
      setError(null);
      setFieldErrors({});

      // Ensure number fields are properly typed before sending to API
      const prepareDataForAPI = (data: any) => {
        const prepared = { ...data };
        if (typeof prepared.creditLimit === 'string') {
          prepared.creditLimit = prepared.creditLimit === '' ? undefined : parseFloat(prepared.creditLimit);
        }
        if (typeof prepared.currentBalance === 'string') {
          prepared.currentBalance = prepared.currentBalance === '' ? undefined : parseFloat(prepared.currentBalance);
        }
        
        // Debug: Log the prepared data to verify types
        console.log('Prepared data for API:', {
          creditLimit: prepared.creditLimit,
          creditLimitType: typeof prepared.creditLimit,
          currentBalance: prepared.currentBalance,
          currentBalanceType: typeof prepared.currentBalance,
        });
        
        return prepared;
      };

      if (id) {
        // Update existing customer
        const updateData: UpdateCustomerDto = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          postalCode: formData.postalCode,
          type: formData.type,
          status: formData.status,
          taxId: formData.taxId,
          website: formData.website,
          notes: formData.notes,
          contactPerson: formData.contactPerson,
          contactPhone: formData.contactPhone,
          contactEmail: formData.contactEmail,
          creditLimit: formData.creditLimit,
          currentBalance: formData.currentBalance,
          paymentTerms: formData.paymentTerms,
        };
        await customersAPI.update(id, prepareDataForAPI(updateData));
      } else {
        // Create new customer
        await customersAPI.create(prepareDataForAPI(formData));
      }
      toastSuccess(id ? 'Customer updated successfully' : 'Customer created successfully');
      navigate('/customers');
    } catch (err: any) {
      console.error('Error saving customer:', err);
      
      // Handle backend validation errors
      if (err.response?.data?.message) {
        const errorData = err.response.data;
        
        // If it's an array of validation errors
        if (Array.isArray(errorData.message)) {
          const newFieldErrors: Record<string, string> = {};
          errorData.message.forEach((errorMsg: string) => {
            // Parse validation error messages like "creditLimit must not be less than 0"
            const fieldMatch = errorMsg.match(/^(\w+)\s+(.+)$/);
            if (fieldMatch) {
              const [, field, message] = fieldMatch;
              newFieldErrors[field] = message;
            } else {
              // If we can't parse the field, add to general error
              setError(errorMsg);
            }
          });
          setFieldErrors(newFieldErrors);
        } else {
          // Single error message
          setError(errorData.message);
        }
      } else {
        setError('Failed to save customer');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
        <p className="mt-1 text-sm text-gray-500">Please select a company to create a customer.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/customers')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Customer' : 'Create New Customer'}
            </h1>
            <p className="text-gray-600">Customer for {selectedCompany.name}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={getFieldClassName('name')}
                />
                {getFieldError('name') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('name')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={getFieldClassName('email')}
                />
                {getFieldError('email') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('email')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Customer Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={CustomerType.INDIVIDUAL}>Individual</option>
                  <option value={CustomerType.BUSINESS}>Business</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={CustomerStatus.ACTIVE}>Active</option>
                  <option value={CustomerStatus.INACTIVE}>Inactive</option>
                  <option value={CustomerStatus.BLOCKED}>Blocked</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Information (shown for business customers) */}
        {formData.type === CustomerType.BUSINESS && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Business Information
            </h3>
            
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Credit Limit ({companyCurrency})
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">{currencySymbol}</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="creditLimit"
                  value={formData.creditLimit || ''}
                  onChange={handleInputChange}
                  className={`pl-7 ${getFieldClassName('creditLimit')}`}
                />
              </div>
              {getFieldError('creditLimit') && (
                <p className="mt-1 text-sm text-red-600">{getFieldError('creditLimit')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Current Balance ({companyCurrency})
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">{currencySymbol}</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  name="currentBalance"
                  value={formData.currentBalance || ''}
                  onChange={handleInputChange}
                  className={`pl-7 ${getFieldClassName('currentBalance')}`}
                />
              </div>
              {getFieldError('currentBalance') && (
                <p className="mt-1 text-sm text-red-600">{getFieldError('currentBalance')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Terms</label>
              <input
                type="text"
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleInputChange}
                placeholder="e.g., Net 30, Due on receipt"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="Additional notes about this customer..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : id ? 'Update Customer' : 'Create Customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
